import type { NumberInputProps } from "@mantine/core";
import { Group, Input, NumberInput, Text, Title } from "@mantine/core";
import { useRichTextEditorContext } from "@mantine/tiptap";
import { useEditorState } from "@tiptap/react";
import { FormFieldInfoText } from "../../../MantineFormFields/FormFieldInfoText/FormFieldInfoText";

interface CustomValuesForTemplateSectionProps {
  invalid?: boolean;
}

function CustomValuesForTemplateSection({
  invalid
}: CustomValuesForTemplateSectionProps) {
  const { editor } = useRichTextEditorContext();

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor?.storage.customValueInTemplate) return null;

      return {
        customValues: editor.storage.customValueInTemplate.values
      };
    }
  });

  if (!editor || !editorState) return null;

  const customValues = editorState.customValues;

  function handleCustomValueChange(
    customValueName: string,
    newValue: string | number
  ) {
    if (!editor) return;

    const newCustomValues = {
      ...customValues,
      [customValueName]: newValue.toString() || null
    };

    editor.storage.customValueInTemplate.values = newCustomValues;
    editor.emit("update", {
      editor,
      transaction: editor.state.tr,
      appendedTransactions: []
    });
  }

  function handleCustomValueFocusChange(
    customValueName: string,
    focusActive: boolean
  ) {
    if (!editor) return;

    // focussing on a custom value input field should highlight that element in the formula text at the same time
    editor.storage.customValueInTemplate.focussed[customValueName] =
      focusActive;
    editor.emit("update", {
      editor,
      transaction: editor.state.tr,
      appendedTransactions: []
    });
  }

  return (
    <>
      <Title order={5} size="sm">
        Vertragsspezifische Variablen
      </Title>
      {Object.keys(customValues).map((customValueName) => (
        <CustomValueInput
          customValueName={customValueName}
          invalid={invalid}
          key={customValueName}
          value={customValues?.[customValueName]}
          onBlur={() => handleCustomValueFocusChange(customValueName, false)}
          onChange={(newValue) =>
            handleCustomValueChange(customValueName, newValue)
          }
          onClear={() => handleCustomValueChange(customValueName, "")}
          onFocus={() => handleCustomValueFocusChange(customValueName, true)}
        />
      ))}
      <FormFieldInfoText
        infoText="Geben Sie einen Wert zu den markierten Variablen ein."
        m={0}
      />
    </>
  );
}

interface CustomValueInputProps
  extends
    Pick<CustomValuesForTemplateSectionProps, "invalid">,
    Pick<NumberInputProps, "onBlur" | "onFocus" | "onChange"> {
  customValueName: string;
  value: string | null;
  onClear: () => void;
}

function CustomValueInput({
  customValueName,
  invalid,
  value,
  onClear,
  ...numberInputHandlers
}: CustomValueInputProps) {
  return (
    <Group gap={6}>
      <Text c={invalid && !value ? "error" : undefined}>
        {customValueName} =
      </Text>
      <NumberInput
        aria-label={customValueName}
        error={invalid && !value}
        rightSection={
          value && value !== "" ? (
            <Input.ClearButton onClick={onClear} />
          ) : undefined
        }
        value={value ?? ""}
        w="100"
        {...numberInputHandlers}
      />
    </Group>
  );
}

export { CustomValuesForTemplateSection };
